'use client';

import { createContext, memo, type ProviderProps, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

export type SelectedProviderContextValue = {
  selectedProvider: string | undefined;
  setSelectedProvider: (provider: string) => void;
};

const selectedProviderDefaultValue: SelectedProviderContextValue = {
  selectedProvider: undefined,
  setSelectedProvider: () => {},
};

const SelectedProviderContext = createContext<SelectedProviderContextValue>(selectedProviderDefaultValue);

export type SelectedProviderContextProviderProps = Pick<ProviderProps<SelectedProviderContextValue>, 'children'>;

export const SelectedProviderContextProvider = memo(({ children }: SelectedProviderContextProviderProps) => {
  const [selectedProvider, setSelectedProvider] = useState<string>();

  const value = useMemo(
    () => ({
      selectedProvider,
      setSelectedProvider,
    }),
    [selectedProvider],
  );

  return <SelectedProviderContext.Provider value={value}>{children}</SelectedProviderContext.Provider>;
});

SelectedProviderContextProvider.displayName = 'SelectedProviderContextProvider';

export const useSelectedProviderContext = () => {
  const context = useContext(SelectedProviderContext);

  if (context === selectedProviderDefaultValue) {
    const error = new Error('useSelectedProviderContext must be used within a SelectedProviderContextProvider');

    if (process.env.NODE_ENV === 'production') {
      reportErrorToBugsnag({ context: 'useSelectedProviderContext used outside provider', error });
    } else {
      throw error;
    }
  }

  return context;
};
